/**
 * Enhanced Product Inventory Management
 * Handles dynamic inventory updates with comprehensive stock types and Shopify policy integration
 * Integrates with existing product-info.js variant change system
 */

if (!customElements.get('product-inventory-enhanced')) {
  customElements.define(
    'product-inventory-enhanced',
    class ProductInventoryEnhanced extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('DOMContentLoaded', this.init.bind(this));
      }

      connectedCallback() {
        this.init();
      }

      init() {
        // Subscribe to variant change events from existing system
        if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
          this.unsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleVariantChange.bind(this)
          );
        }

        // V2 variant selector handles its own DOM updates via section rendering.
        // We do NOT listen to v2:variantChange here because:
        // 1. Section rendering provides complete variant data including metafields
        // 2. Listening here would cause a race condition where we update with partial
        //    data (no metafields for Storefront API variants), showing generic "In Stock."
        //    before section rendering replaces it with the proper lead time message
      }

      disconnectedCallback() {
        if (this.unsubscriber) {
          this.unsubscriber();
        }
      }

      handleVariantChange({ data: { selectedVariant } }) {
        if (!selectedVariant) return;

        // Add fade-out effect before updating
        const inventoryElement = this.querySelector('.product__inventory');
        if (inventoryElement) {
          inventoryElement.style.opacity = '0.5';
          inventoryElement.style.transition = 'opacity 0.3s ease';
        }

        // Update inventory display after short delay for smooth transition
        setTimeout(() => {
          this.updateInventoryDisplay(selectedVariant);
          
          // Fade back in
          if (inventoryElement) {
            inventoryElement.style.opacity = '1';
          }
        }, 150);
      }

      updateInventoryDisplay(variant) {
        const inventoryElement = this.querySelector('.product__inventory');
        if (!inventoryElement) return;

        // Get block settings from data attributes
        const stockTypeKey = inventoryElement.dataset.stockTypeKey || 'stock_type';
        const leadTimeKey = inventoryElement.dataset.leadTimeKey || 'shipping_lead_time';
        const showFreeShipping = inventoryElement.dataset.showFreeShipping !== 'false';
        const freeShippingThreshold = parseInt(inventoryElement.dataset.freeShippingThreshold) || 75;

        // Get variant metafields and properties
        const stockType = variant.metafields?.custom?.[stockTypeKey];
        const leadTime = variant.metafields?.custom?.[leadTimeKey];
        const quantity = variant.inventory_quantity || 0;
        const inventoryPolicy = variant.inventory_policy;
        const inventoryManagement = variant.inventory_management;
        const price = variant.price || 0;

        // Determine inventory status and styling
        const inventoryInfo = this.determineInventoryInfo({
          stockType,
          leadTime,
          quantity,
          inventoryPolicy,
          inventoryManagement
        });

        // Update DOM elements
        this.updateInventoryDOM(inventoryElement, inventoryInfo, {
          leadTime,
          showFreeShipping,
          freeShippingThreshold,
          price
        });
      }

      determineInventoryInfo({ stockType, quantity, inventoryPolicy, inventoryManagement }) {
        let status = 'in-stock';
        let message = 'In Stock.';
        let icon = 'fa-check-circle';
        let showLeadTime = false;
        let reserveMessage = '';

        // Priority 1: Made To Order (highest priority)
        if (stockType === 'Made To Order') {
          status = 'made-to-order';
          icon = 'fa-box-heart';
          message = 'Made To Order.';
          showLeadTime = true;

        // Priority 2-3: Discontinued - Final Sale (check quantity)
        } else if (stockType === 'Discontinued - Final Sale') {
          status = 'discontinued';
          icon = 'fa-siren-on';
          showLeadTime = true;
          if (quantity > 0) {
            // Priority 2: Discontinued WITH stock
            message = 'In Stock - Closeout - Final Sale.';
          } else {
            // Priority 3: Discontinued WITHOUT stock
            message = 'Closeout - Final Sale.';
          }

        // Priority 4: Explicit Backorder - show lead time only (no status text)
        } else if (stockType === 'Backorder') {
          status = 'backorder-explicit';
          icon = 'fa-box-open';
          message = '';
          showLeadTime = true;

        // Priority 5-8: Quantity-based logic (no explicit stock_type)
        } else if (inventoryManagement === 'shopify') {
          if (quantity > 0) {
            // Priority 5: In Stock (any positive quantity — no low-stock state,
            // matching the Liquid render paths on PDP and cart)
            status = 'in-stock';
            icon = 'fa-check-circle';
            message = 'In Stock.';
            showLeadTime = true;
          } else if (inventoryPolicy === 'continue') {
            // Priority 7: Backordered (qty < 1, can still sell)
            status = 'backordered';
            icon = 'fa-box-open';
            message = 'Backordered.';
            reserveMessage = 'Order today to reserve stock.';
            // Only show lead time if stock_type confirms backorder (lead time updated by inventory system).
            // When Shopify flags backorder from stock decrement but stock_type is not 'Backorder',
            // the lead time still reflects in-stock shipping and would be misleading.
            showLeadTime = stockType === 'Backorder';
          } else {
            // Priority 8: Out of Stock (qty < 1, cannot sell)
            status = 'out-of-stock';
            icon = 'fa-times-circle';
            message = 'Out of stock';
          }
        } else {
          // Not tracking inventory - assume in stock
          status = 'not-tracked';
          icon = 'fa-check-circle';
          message = 'In Stock.';
        }

        return { status, message, icon, showLeadTime, reserveMessage, quantity };
      }

      /**
       * Strip a leading "Backordered" prefix and any case variant of
       * "Estimated to ship in/after" / "Ships in/after" prefixes from a
       * lead-time string. Mirrors the Liquid prefix-strip in
       * inventory-stock-notice.liquid.
       *
       * Returns { bare, preposition, isProse }. preposition is 'after'
       * when merch authored an "after <date>" phrasing (typical
       * backorder), else 'in'. isProse is true when bare looks like a
       * full-sentence status message ("We will fulfill...") rather than
       * a duration/date — callers should skip the ship-time wrapper.
       */
      stripLeadTimePrefix(leadTime) {
        if (!leadTime) return { bare: '', preposition: 'in', isProse: false };
        let bare = String(leadTime).trim();

        // Strip "Backordered" prefix in all the separator shapes merch authors.
        bare = bare
          .replace(/^\s*Backordered\s*[-–—:.]\s*/i, '')
          .replace(/^\s*Backordered\s+/i, '')
          .replace(/^\s*Backordered$/i, '')
          .trim();

        const preposition = /\b(ship|ships|available)\s+after\b/i.test(bare) ? 'after' : 'in';

        bare = bare
          .replace(/^\s*Estimated to ship in\s+/i, '')
          .replace(/^\s*Estimated to ship after\s+/i, '')
          .replace(/^\s*Estimated to ship\s+/i, '')
          .replace(/^\s*Ships in\s+/i, '')
          .replace(/^\s*Ships after\s+/i, '')
          .trim()
          .replace(/\.\s*$/, ''); // strip trailing sentence period — leaves "1.5 weeks" alone

        // Narrow phrase allowlist mirrors inventory-stock-notice.liquid.
        const isProse = /\b(we will|as soon as|back in stock|please contact|contact us|currently unavailable|will fulfill|when available|once available|no eta)\b/i.test(bare);

        return { bare, preposition, isProse };
      }

      updateInventoryDOM(inventoryElement, inventoryInfo, options) {
        // Mirrors the canonical render in theme/snippets/inventory-stock-notice.liquid — keep them in sync.
        const { status, message, icon, showLeadTime, reserveMessage } = inventoryInfo;
        const { leadTime } = options;

        // Update main inventory status
        inventoryElement.className = `product__inventory product__inventory--${status} font-body-bolder`;
        inventoryElement.setAttribute('data-inventory-status', status);

        // Update icon
        const iconElement = inventoryElement.querySelector('.fas');
        if (iconElement) {
          iconElement.className = `fas ${icon}`;
        }

        // Update text content
        const textElement = inventoryElement.querySelector('.product__inventory-text');
        if (textElement) {
          let html = '';
          const isBackordered = status === 'backordered' || status === 'backorder-explicit';
          const { bare, preposition, isProse } = this.stripLeadTimePrefix(leadTime);

          if (isBackordered) {
            html += `<strong>Backordered.</strong>`;
            if (showLeadTime && bare) {
              html += isProse
                ? ` <span class="product__inventory-lead-time-inline">${bare}</span>`
                : ` <span class="product__inventory-lead-time-inline">Estimated to ship ${preposition} ${bare}</span>`;
            }
          } else if (status === 'made-to-order' || status === 'custom-made' || status === 'discontinued') {
            html += `<strong>${message}</strong>`;
            if (showLeadTime && bare) {
              html += isProse
                ? ` <span class="product__inventory-lead-time-inline">${bare}</span>`
                : ` <span class="product__inventory-lead-time-inline">Estimated to ship ${preposition} ${bare}</span>`;
            }
          } else if (status === 'in-stock' || status === 'not-tracked') {
            html += `<strong>${message}</strong>`;
            if (isProse && bare) {
              html += ` <span class="product__inventory-lead-time-inline">${bare}</span>`;
            } else {
              // Match en.default.json literal storage of inventory_default_ship_time
              const defaultShipTime = '1&ndash;3 business days';
              html += ` <span class="product__inventory-lead-time-inline">Ships ${preposition} ${bare || defaultShipTime}</span>`;
            }
          } else if (message) {
            html += `<strong>${message}</strong>`;
          }

          // Add reserve message for backordered items
          if (reserveMessage) {
            html += `<span class="product__inventory-reserve-message"> ${reserveMessage}</span>`;
          }

          textElement.innerHTML = html;
        }
      }
    }
  );
}

// Auto-initialize enhanced inventory on page load
document.addEventListener('DOMContentLoaded', () => {
  const inventoryElements = document.querySelectorAll('[id^="Inventory-"]');
  inventoryElements.forEach(element => {
    if (!element.closest('product-inventory-enhanced')) {
      const enhancedWrapper = document.createElement('product-inventory-enhanced');
      element.parentNode.insertBefore(enhancedWrapper, element);
      enhancedWrapper.appendChild(element);
    }
  });
});